"use client";
// src/contexts/AuthContext.tsx

import React, { 
  createContext, 
  use,
  useState, 
  useEffect, 
  useCallback, 
  useMemo,
  useDeferredValue,
  useTransition,
  useId,
  useRef
} from 'react';
import { useRouter } from 'next/navigation';
import { getAuth } from '@/lib/firebase/core';
import { userManager, sessionManager } from '@/services';
import { isSampleDataMode } from '@/lib/config/env';
import type { User, UserRoleType } from '@/lib/types';
import { LoginOverlay, LogoutOverlay } from '@/components/ui/auth-overlay';
import { mapFirebaseError, type AuthError } from '@/lib/error-handling';

// =============================================================================
// TYPES AND INTERFACES
// =============================================================================

export interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  loginError: AuthError | null;
  isLoggingIn: boolean;
  loggingOut: boolean;
  userRole: UserRoleType | 'Guest';
  hasRole: (role: UserRoleType) => boolean;
  hasAnyRole: (roles: UserRoleType[]) => boolean;
  login: (email: string, password: string) => Promise<void>;
  loginAsGuest: () => Promise<void>;
  signup: (firstName: string, lastName: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => Promise<void>;
  // React 19: Enhanced context tracking
  instanceId: string;
  lastActivity: number;
  authInitialized: boolean;
}

// =============================================================================
// CONSTANTS AND HELPERS
// =============================================================================


const SESSION_POLL_INTERVAL = 60000; // 60 seconds (increased from 30 seconds)
const LOGIN_STATE_CLEAR_DELAY = 1000; // 1 second (reduced from 2 seconds)
// const PERFORMANCE_LOG_THRESHOLD = 1000; // Log operations taking longer than 1s

/**
 * Route determination with memoization
 */
const getRouteForUser = (user: User): string => {
  // Always redirect all users to dashboard regardless of role
  return '/dashboard';
};

/**
 * Auth error creation
 */
const createAuthError = (code: string, i18nKey: string, options: Partial<AuthError> = {}): AuthError => ({
  code,
  i18nKey,
  severity: 'error',
  category: 'auth',
  retryable: true,
  userAction: 'retry',
  ...options,
});

// =============================================================================
// ENHANCED AUTH CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Generate stable instance ID
  const instanceId = useId();
  
  // Core state with transitions
  const [sessionState, setSessionState] = useState(sessionManager.getSessionState());
  const [loginError, setLoginError] = useState<AuthError | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [authInitialized, setAuthInitialized] = useState(false);
  
  // Transitions for non-urgent updates
  const [, startTransition] = useTransition();
  
  // Deferred values for better performance
  const deferredUser = useDeferredValue(sessionState.user);
  const deferredError = useDeferredValue(loginError);
  
  // Refs for cleanup and performance
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // const performanceTimerRef = useRef<number>(0);
  
  const router = useRouter();

  // =============================================================================
  // ENHANCED HELPER FUNCTIONS
  // =============================================================================

  /**
   * Performance-tracked user data loading
   */
  const loadUserData = useCallback(async () => {
    try {
      if (isSampleDataMode()) {
        return;
      }
      
      const auth = getAuth();
      const firebaseUser = auth?.currentUser;
      
      if (firebaseUser?.email) {
        const userData = await userManager.getUserByEmail(firebaseUser.email);
        if (userData) {
          startTransition(() => {
            sessionManager.updateSessionState({ user: userData });
            setLastActivity(Date.now());
          });
        }
      }
    } catch (error) {
      console.error(`[Auth:${instanceId}] Error loading user data:`, error);
    }
  }, [instanceId]);

  /**
   * Auth error creation
   */
  const handleFirebaseAuth = useCallback(async (email: string, password: string) => {
    const auth = getAuth();
    if (!auth) {
      throw createAuthError(
        'auth/configuration-error', 
        'auth.errors.configurationError',
        { category: 'system', retryable: false, userAction: 'contactSupport' }
      );
    }
    
    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      await signInWithEmailAndPassword(auth, email, password);
      
      // Wait a moment for auth state to settle
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const userData = await userManager.getUserByEmail(email);
      if (!userData) {
        // Check if we're in sample data mode to provide a more helpful error
        if (isSampleDataMode()) {
          throw createAuthError(
            'auth/sample-data-user-not-found', 
            'auth.errors.sampleDataUserNotFound',
            { 
              category: 'auth', 
              retryable: false, 
              userAction: 'useSampleCredentials'
            }
          );
        } else {
          throw createAuthError('auth/user-not-found', 'auth.errors.userNotFound');
        }
      }
      
      return userData;
    } catch (error: any) {
      throw error;
    }
  }, []);



  // =============================================================================
  // AUTHENTICATION FUNCTIONS
  // =============================================================================

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    setLoginError(null);
    
    try {
      console.log('🔐 Starting login process...');
      
      // Always use Firebase Auth - sample data mode only affects non-auth entities
      const userData = await handleFirebaseAuth(email, password);
      
      // Log successful authentication using Firebase UID (secure)
      console.log(`✅ User authenticated successfully - UID: ${userData.uid}, Roles:`, userData.roles);
      
      // Create session cookie for server-side authentication
      try {
        const auth = getAuth();
        const currentUser = auth?.currentUser;
        if (currentUser) {
          console.log('🍪 Creating session cookie...');
          const idToken = await currentUser.getIdToken();
          const response = await fetch('/api/auth/set-session', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ idToken }),
          });
          
          if (response.ok) {
            const responseData = await response.json();
            console.log('✅ Session cookie created successfully:', responseData);
          } else {
            const errorData = await response.text();
            console.error('❌ Session cookie creation failed:', response.status, errorData);
          }
        } else {
          console.error('❌ No current user found for session cookie creation');
        }
      } catch (sessionError) {
        console.warn('⚠️ Failed to create session cookie:', sessionError);
        // Continue with login even if session cookie creation fails
      }
      
      // CRITICAL FIX: Update the session state with user data
      console.log('🔄 Updating session state with user data...');
      sessionManager.updateSessionState({ user: userData });
      
      // IMPORTANT: Force a state update to ensure the context reflects the new session state
      const updatedSessionState = sessionManager.getSessionState();
      console.log('📊 Session state after update:', {
        isAuthenticated: updatedSessionState.isAuthenticated,
        hasUser: !!updatedSessionState.user,
        userRoles: updatedSessionState.user?.roles,
        userUid: updatedSessionState.user?.uid
      });
      
      // Force update the local state to ensure immediate synchronization
      setSessionState(updatedSessionState);
      
      // Add a longer delay to ensure all state updates are propagated
      console.log('⏳ Waiting for state synchronization...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verify the state is properly set before navigation
      sessionManager.getSessionState();
      // Session state updated
      
      // Determine navigation route
      const route = getRouteForUser(userData);
      console.log('🎯 Determined route for user:', route);
      
      // Navigate directly using router.push with force refresh
      console.log('🔄 Navigating user to:', route);
      await router.push(route);
      
      // Additional verification after navigation
      console.log('✅ Navigation completed. Current URL should be:', route);
      
      // Clear login state after successful navigation
      setIsLoggingIn(false);
      
    } catch (error: unknown) {
      console.error('❌ Login error:', error);
      
      if (error && typeof error === 'object' && 'code' in error && 'i18nKey' in error && 'severity' in error) {
        setLoginError(error as AuthError);
      } else {
        const mappedError = mapFirebaseError(error as { code?: string });
        setLoginError(mappedError);
      }
      setIsLoggingIn(false);
    }
  }, [isLoggingIn, handleFirebaseAuth, router, instanceId]);

  /**
   * Guest login function - automatically logs in as guest user
   * No credentials exposed to UI - perfect for "Continue as Guest" functionality
   */
  const loginAsGuest = useCallback(async (): Promise<void> => {
    if (isLoggingIn) return;
    
    console.log('👤 Starting guest login...');
    
    // Use the predefined guest credentials from sample data
    const guestEmail = 'guest@example.com';
    const guestPassword = 'SamplePassword123!';
    
    // Reuse the existing login function with guest credentials
    await login(guestEmail, guestPassword);
  }, [login, isLoggingIn]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setLoggingOut(true);
      
      // Start the minimum overlay display timer
      const minOverlayTime = 1000; // 1 second minimum
      const startTime = Date.now();
      
      // Clear server-side session cookies first
      try {
        const response = await fetch('/api/auth/clear-token', { method: 'POST' });
        if (response.ok) {
          console.log('✅ Server-side session cleared successfully');
        } else {
          console.warn('⚠️ Server-side session clear failed:', response.status);
        }
      } catch (error) {
        console.warn('⚠️ Failed to clear server-side session:', error);
      }
      
      // Clear Firebase Auth
      const auth = getAuth();
      if (auth) {
        try {
          const { signOut } = await import('firebase/auth');
          await signOut(auth);
          console.log('✅ Firebase Auth signed out successfully');
        } catch (error) {
          console.warn('⚠️ Firebase Auth sign out failed:', error);
        }
      }
      
      // Clear client-side session state
      sessionManager.clearSession();
      console.log('✅ Client-side session cleared');
      
      // Clear any additional browser storage
      if (typeof window !== 'undefined') {
        try {
          // Clear localStorage items
          localStorage.removeItem('authRedirect');
          localStorage.removeItem('lastRoute');
          localStorage.removeItem('pendingRedirect');
          localStorage.removeItem('courtlyUser');
          
          // Clear sessionStorage items
          sessionStorage.removeItem('authRedirect');
          sessionStorage.removeItem('lastRoute');
          sessionStorage.removeItem('pendingRedirect');
          sessionStorage.removeItem('courtlyUser');
          
          console.log('✅ Browser storage cleared');
        } catch (error) {
          console.warn('⚠️ Failed to clear browser storage:', error);
        }
      }
      
      // Ensure overlay is shown for at least 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minOverlayTime - elapsedTime);
      
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      // Force redirect to login page - keep overlay visible during redirect
      if (typeof window !== 'undefined') {
        console.log('🔄 Redirecting to login page...');
        window.location.href = '/login';
      }
      
      // Note: We don't set setLoggingOut(false) here because the page will redirect
      // and the component will unmount. This keeps the overlay visible during redirect.
      
    } catch (error) {
      console.error(`❌ Logout error:`, error);
      
      // Even on error, ensure cleanup
      sessionManager.clearSession();
      
      if (typeof window !== 'undefined') {
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (storageError) {
          console.warn('Failed to clear storage on error:', storageError);
        }
      }
      
      // Even on error, ensure minimum overlay time
      const minOverlayTime = 1000;
      await new Promise(resolve => setTimeout(resolve, minOverlayTime));
      
      // Force redirect even on error - keep overlay visible
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      
      // Note: We don't set setLoggingOut(false) here either, for the same reason
    }
  }, [instanceId]);

  const signup = useCallback(async (firstName: string, lastName: string, email: string, password: string): Promise<void> => {
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    setLoginError(null);
    
    try {
      console.log('📝 Starting signup process');
      
      // Call our signup API
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName,
          lastName,
          email,
          password,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        console.error('❌ Signup failed:', data.error);
        throw createAuthError(
          data.error === 'User already exists' ? 'auth/email-already-in-use' : 'auth/signup-failed',
          data.error === 'User already exists' ? 'auth.errors.emailAlreadyInUse' : 'auth.errors.signupFailed',
          { 
            category: 'auth',
            retryable: data.error !== 'User already exists',
            userAction: data.error === 'User already exists' ? 'useExistingAccount' : 'retry'
          }
        );
      }
      
      console.log('✅ Signup successful:', data.message);
      
      // After successful signup, automatically log the user in
      await login(email, password);
      
    } catch (error: any) {
      console.error('❌ Signup error:', error);
      
      // Handle different error types
      if (error.code) {
        setLoginError(error);
      } else {
        setLoginError(createAuthError(
          'auth/signup-failed',
          'auth.errors.signupFailed',
          { category: 'auth', retryable: true, userAction: 'retry' }
        ));
      }
    } finally {
      // Don't set isLoggingIn to false here if login was successful,
      // as the login process will handle the state transition
      if (loginError) {
        setIsLoggingIn(false);
      }
    }
  }, [isLoggingIn, login]);

  const updateUser = useCallback(async (userData: Partial<User>): Promise<void> => {
    if (!sessionState.user) {
      throw new Error('No user logged in');
    }
    
    try {
      const updatedUser = { ...sessionState.user, ...userData };
      startTransition(() => {
        sessionManager.updateSessionState({ user: updatedUser });
        setLastActivity(Date.now());
      });
    } catch (error) {
      console.error(`[Auth:${instanceId}] Update user error:`, error);
      throw error;
    }
  }, [sessionState.user, instanceId]);

  // =============================================================================
  // ENHANCED ROLE HELPERS
  // =============================================================================

  const userRole = useMemo((): UserRoleType | 'Guest' => {
    if (!deferredUser?.roles?.length) return 'Guest';
    
    const roles = deferredUser.roles;
    const roleHierarchy: UserRoleType[] = [
      'Super Admin', 'Admin', 'Staff', 'Teacher', 
      'Tennis School', 'Tennis Club', 'Member'
    ];
    
    // Optimized role finding
    for (const role of roleHierarchy) {
      if (roles.includes(role)) return role;
    }
    
    return 'Guest';
  }, [deferredUser?.roles]);

  const hasRole = useCallback((role: UserRoleType): boolean => {
    return deferredUser?.roles?.includes(role) || false;
  }, [deferredUser?.roles]);

  const hasAnyRole = useCallback((roles: UserRoleType[]): boolean => {
    return roles.some(role => hasRole(role));
  }, [hasRole]);

  // =============================================================================
  // ENHANCED EFFECTS
  // =============================================================================

  // Optimized session polling with cleanup
  useEffect(() => {
    const pollSessionState = () => {
      const startTime = performance.now();
      
      try {
        const currentState = sessionManager.getSessionState();
        
        // Only update state if there are meaningful changes
        if (
          currentState.isAuthenticated !== sessionState.isAuthenticated ||
          currentState.user?.uid !== sessionState.user?.uid ||
          (currentState.accessToken !== sessionState.accessToken && currentState.accessToken !== null)
        ) {
          setSessionState(currentState);
        }
        
        // Only load user data if authenticated but no user data exists
        if (currentState.isAuthenticated && !currentState.user) {
          loadUserData();
        }
      } finally {
        const duration = performance.now() - startTime;
        if (duration > 100) { // Log if session check takes more than 100ms
          console.warn(`[Auth:${instanceId}] Slow session check: ${duration.toFixed(2)}ms`);
        }
      }
    };

    // Only start polling if we're authenticated or in the process of authenticating
    if (sessionState.isAuthenticated || isLoggingIn) {
      pollIntervalRef.current = setInterval(pollSessionState, SESSION_POLL_INTERVAL);
      pollSessionState(); // Initial load
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [loadUserData, instanceId, sessionState.isAuthenticated, sessionState.user?.uid, sessionState.accessToken, isLoggingIn]);

  // Enhanced login state clearing - only for error cases now
  useEffect(() => {
    if (loginError && isLoggingIn) {
      // Clear login state after error is shown
      const timer = setTimeout(() => {
        startTransition(() => {
          setIsLoggingIn(false);
        });
      }, LOGIN_STATE_CLEAR_DELAY);
      
      return () => clearTimeout(timer);
    }
  }, [loginError, isLoggingIn]);

  // Activity tracking
  useEffect(() => {
    const handleActivity = () => {
      startTransition(() => {
        setLastActivity(Date.now());
      });
    };

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, []);

  // Subscribe to sessionManager events to keep sessionState in sync
  useEffect(() => {
    const unsubscribe = sessionManager.subscribe(() => {
      setSessionState(sessionManager.getSessionState());
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // Track Firebase auth initialization
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const auth = getAuth();
    if (!auth) {
      setAuthInitialized(true);
      return;
    }
    
    let hasInitialized = false;
    
    // Set up a timeout fallback in case Firebase auth is slow to initialize
    const initTimeout = setTimeout(() => {
      if (!hasInitialized) {
        console.warn('⏰ Firebase auth initialization timeout - proceeding without full auth state');
        setAuthInitialized(true);
        hasInitialized = true;
      }
    }, 3000); // 3 second timeout
    
    // Firebase auth initialization tracking with stability delay
    const unsubscribe = auth.onAuthStateChanged(() => {
      // Mark auth as initialized after first auth state change with a small delay
      // This ensures Firebase auth is fully settled before we show guest access
      if (!hasInitialized) {
        setTimeout(() => {
          setAuthInitialized(true);
          hasInitialized = true;
          clearTimeout(initTimeout);
        }, 100); // Small delay to ensure auth state is stable
      }
    });
    
    return () => {
      clearTimeout(initTimeout);
      unsubscribe();
    };
  }, []);

  // =============================================================================
  // ENHANCED COMPUTED VALUES
  // =============================================================================

  const value: AuthContextType = useMemo(() => {
    const contextLoading = (sessionState.isAuthenticated && !sessionState.user && !loggingOut) || isLoggingIn;
    const contextAuthenticated = sessionState.isAuthenticated && !!sessionState.user;
    
    return {
      user: deferredUser,
      loading: contextLoading,
      isAuthenticated: contextAuthenticated,
      loginError: deferredError,
      isLoggingIn: isLoggingIn,
      loggingOut,
      userRole,
      hasRole,
      hasAnyRole,
      login,
      loginAsGuest,
      signup,
      logout,
      updateUser,
      instanceId,
      lastActivity,
      authInitialized,
    };
  }, [
    deferredUser,
    sessionState.isAuthenticated,
    sessionState.user,
    loggingOut,
    deferredError,
    isLoggingIn,
    userRole,
    hasRole,
    hasAnyRole,
    login,
    loginAsGuest,
    signup,
    logout,
    updateUser,
    instanceId,
    lastActivity,
    authInitialized,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoginOverlay isVisible={isLoggingIn} />
      <LogoutOverlay isVisible={loggingOut} />
    </AuthContext.Provider>
  );
}

// =============================================================================
// ENHANCED HOOK
// =============================================================================

export function useAuth() {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
